import { useEffect, useState } from "react";
import Modal from "./Modal";
import InlineEmailOtpVerification from "./InlineEmailOtpVerification";

const getNameParts = (fullName) => {
  const trimmedName = fullName.trim();
  if (!trimmedName) {
    return { firstName: "", lastName: "" };
  }

  const [firstName, ...rest] = trimmedName.split(/\s+/);

  return {
    firstName,
    lastName: rest.join(" ") || "Brochure Lead",
  };
};

export default function BrochureLeadModal({
  isOpen,
  onClose,
  brochureUrl,
  programId,
  programName,
  format,
}) {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: "", email: "" });
      setStatus("idle");
      setMessage("");
      setVerifiedEmail("");
    }
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { firstName, lastName } = getNameParts(formData.name);
    if (!firstName || !formData.email) {
      return;
    }

    if (verifiedEmail !== formData.email.trim().toLowerCase()) {
      setStatus("error");
      setMessage("Please verify your email with OTP before downloading the brochure.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch("/api/enroll/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          programId,
          programName,
          format,
          firstName,
          lastName,
          email: formData.email,
          goals: `Brochure download request for ${programName}${format ? ` (${format})` : ""}.`,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Unable to submit brochure request.");
      }

      setStatus("success");
      setMessage("Brochure is ready. Starting your download...");

      const link = document.createElement("a");
      link.href = brochureUrl;
      link.download = brochureUrl.split("/").pop() || "brochure.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <span className="inline-block px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-[0.18em] mb-3">
            Brochure Access
          </span>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-brand-dark tracking-tight mb-3">
            Download the brochure
          </h3>
          <p className="text-sm sm:text-base text-gray-500 leading-relaxed">
            Share your name and email to receive the {programName} brochure.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="brochure-name" className="block text-sm font-bold text-brand-dark mb-2">
              Full Name
            </label>
            <input
              id="brochure-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm text-brand-dark focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/10"
              required
              disabled={status === "loading"}
            />
          </div>

          <div>
            <label htmlFor="brochure-email" className="block text-sm font-bold text-brand-dark mb-2">
              Email Address
            </label>
            <input
              id="brochure-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm text-brand-dark focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/10"
              required
              disabled={status === "loading"}
            />
            <div className="mt-3">
              <InlineEmailOtpVerification
                email={formData.email}
                verifiedEmail={verifiedEmail}
                onVerifiedEmailChange={setVerifiedEmail}
                context="brochure"
                mode="manual"
              />
            </div>
          </div>

          {message && (
            <div
              className={`rounded-md border px-4 py-3 text-sm font-medium ${
                status === "error"
                  ? "border-rose-100 bg-rose-50 text-rose-700"
                  : "border-emerald-100 bg-emerald-50 text-emerald-700"
              }`}
            >
              {message}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-3 rounded-md border border-gray-200 text-sm font-bold text-brand-dark hover:bg-gray-50 transition-colors"
              disabled={status === "loading"}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-5 py-3 rounded-md bg-brand-primary text-sm font-bold text-white hover:shadow-lg hover:shadow-brand-primary/20 transition-all disabled:opacity-50"
              disabled={status === "loading" || verifiedEmail !== formData.email.trim().toLowerCase()}
            >
              {status === "loading" ? "Submitting..." : "Download Brochure"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
